import { TinyColor } from '@ctrl/tinycolor'
import { format as formatText } from '@operato/utils/format.js'

/**
 * v2 형식의 scene-chart config를 Chart.js v4 형식으로 변환한다.
 * 기존 보드 설정(v2 형식)을 입력받아 v4 호환 config를 새 객체로 반환.
 */
export function convertConfigure(chart: SceneChart.ChartConfig): SceneChart.ChartConfig {
  if (!chart) return chart

  const { type: chartType, data: fromData, options: fromOptions } = chart
  const options = fromOptions || ({} as SceneChart.ChartOptions)
  const datasets = fromData?.datasets || []

  const fontSize = Number(options.defaultFontSize) || undefined
  const fontFamily = options.defaultFontFamily
  const fontColor = options.defaultFontColor
  const theme = options.theme
  const multiAxis = options.multiAxis
  const stacked = options.stacked
  const xGridLine = options.xGridLine
  const yGridLine = options.yGridLine
  const y2ndGridLine = options.y2ndGridLine
  const showTooltip = options.tooltip !== false

  // backward compatible: ensure scales/scale exist
  _ensureBackwardsCompatible(chartType, options)

  const scales = options.scales || {}
  const { xAxes = [], yAxes = [] } = scales

  // --- Build v4 scales object ---
  const toScales: Record<string, any> = {}
  const isCartesian = ['line', 'bar', 'horizontalBar'].includes(chartType)

  if (isCartesian) {
    // Determine actual xAxes/yAxes depending on horizontalBar swap
    const actualXAxes = chartType === 'horizontalBar' ? yAxes : xAxes
    const actualYAxes = chartType === 'horizontalBar' ? xAxes : yAxes

    actualXAxes.forEach((xAxis, index) => {
      const id = actualXAxes.length > 1 ? `x${index + 1}` : 'x'
      toScales[id] = _buildScale(
        {
          axis: 'x',
          id,
          position: 'bottom',
          display: xAxis.display !== false,
          title: {
            display: !!xAxis.axisTitle,
            text: xAxis.axisTitle
          },
          grid: {
            display: chartType === 'horizontalBar' ? yGridLine : xGridLine
          },
          ticks: xAxis.ticks,
          stacked: stacked || false
        },
        { fontSize, fontFamily, fontColor, theme }
      )
    })

    actualYAxes.forEach((yAxis, index) => {
      const id = yAxis.id || (index === 0 ? 'left' : 'right')
      const isSecondary = index > 0 || id === 'right'
      const display = isSecondary ? (multiAxis || false) : (yAxis.display !== false)

      toScales[id] = _buildScale(
        {
          axis: 'y',
          id,
          position: yAxis.position || id,
          display,
          title: {
            display: !!yAxis.axisTitle,
            text: yAxis.axisTitle
          },
          grid: {
            display: isSecondary ? y2ndGridLine : yGridLine
          },
          ticks: yAxis.ticks,
          stacked: stacked || false
        },
        { fontSize, fontFamily, fontColor, theme }
      )
    })
  }

  // radar/polarArea: handle scale (radial)
  if (chartType === 'radar' || chartType === 'polarArea') {
    const scale = options.scale || {}
    toScales['r'] = {
      type: 'radialLinear',
      ticks: {
        ...(scale.ticks || {}),
        backdropColor: options.fillStyle || '#00ff0000'
      },
      pointLabels: {
        font: { size: fontSize, family: fontFamily },
        ...(fontColor ? { color: fontColor } : {})
      }
    }
    if (fontColor) {
      toScales['r'].ticks.color = fontColor
    }
  }

  // --- Build v4 datasets ---
  // axis → dataset 속성 마이그레이션 (bar thickness 등)
  let barCategoryPercentage: number | undefined
  let barPercentage: number | undefined
  let barThickness: number | undefined

  if (chartType === 'bar' || chartType === 'horizontalBar') {
    const barAxis = chartType === 'bar' ? xAxes[0] : yAxes[0]
    if (barAxis) {
      barCategoryPercentage = barAxis.categorySpacing != null ? 1 - barAxis.categorySpacing : undefined
      barPercentage = barAxis.barSpacing != null ? 1 - barAxis.barSpacing : undefined
      barThickness = (barAxis as any).barThickness
    }
  }

  const toDatasets = datasets.map((dataset, index) => {
    const series = { ...dataset }

    // horizontalBar series type → bar
    if (series.type === 'horizontalBar') {
      series.type = 'bar'
    }

    // multiAxis off: all series to left axis
    if (!multiAxis && series.yAxisID === 'right') {
      series.yAxisID = 'left'
    }

    // bar spacing properties (moved from axis to dataset in v4)
    if (chartType === 'bar' || chartType === 'horizontalBar') {
      if (barCategoryPercentage != null) series.categoryPercentage = barCategoryPercentage
      if (barPercentage != null) series.barPercentage = barPercentage
      if (barThickness != null) series.barThickness = barThickness
    }

    // stack: v4 format
    const isPieType = chartType === 'pie' || chartType === 'doughnut'
    if (!isPieType) {
      if (stacked && !dataset.stack) {
        series.stack = '__all__'
      } else {
        const type = series.type || chartType
        series.stack = `__${type}_${series.yAxisID || 'left'}_${index}__`
      }
    }

    // series visual setup
    _setupSeriesVisuals(series, chartType)

    return series
  })

  // --- Build converted config ---
  const converted: SceneChart.ChartConfig = {
    type: chartType === 'horizontalBar' ? 'bar' : chartType,
    data: {
      labels: fromData?.labels || [],
      datasets: toDatasets as any,
      labelDataKey: fromData?.labelDataKey
    },
    options: {
      maintainAspectRatio: false,
      animation: options.animation,
      indexAxis: chartType === 'horizontalBar' ? 'y' : undefined,
      scales: isCartesian || chartType === 'radar' || chartType === 'polarArea' ? toScales : undefined,
      plugins: {
        legend: _buildLegend(options.legend || {}, { fontSize, fontFamily, fontColor, theme }),
        tooltip: showTooltip
          ? _buildTooltip({ fontSize, fontFamily })
          : { enabled: false },
      },
      // preserve custom properties for datalabels font callback
      defaultFontFamily: fontFamily,
      defaultFontSize: fontSize,
      defaultFontColor: fontColor
    }
  }

  return converted
}

// --- Helper functions ---

function _ensureBackwardsCompatible(type: string, options: SceneChart.ChartOptions) {
  switch (type) {
    case 'horizontalBar':
      if (!options.scales) options.scales = {}
      break
    case 'radar':
    case 'polarArea':
      if (!options.scale) options.scale = {}
      if (!options.scale.ticks) options.scale.ticks = {}
      break
    case 'line':
    case 'bar':
      if (!options.scales) options.scales = {}
      if (!options.scales.yAxes) options.scales.yAxes = []

      if (options.scales.yAxes.length === 1) {
        options.scales.yAxes.push({
          position: 'right',
          id: 'right',
          display: options.multiAxis || false,
          gridLines: {
            display: (options.scales.yAxes[0]?.gridLines?.display) || false
          },
          ticks: {
            beginAtZero: false
          }
        })
      }
      break
    case 'pie':
    case 'doughnut':
      break
    default:
      if (!options.scale) options.scale = {}
      break
  }
}

function _buildScale(
  axis: any,
  { fontSize, fontFamily, fontColor, theme }: { fontSize?: number; fontFamily?: string; fontColor?: string; theme?: string }
): any {
  const ticks = axis.ticks ? { ...axis.ticks } : {}
  const baseColor = _getBaseColorFromTheme(theme)

  // v4: min/max moved from ticks to scale level
  const result: any = {
    axis: axis.axis,
    position: axis.position,
    display: axis.display,
    stacked: axis.stacked,
    title: axis.title || {},
    grid: {
      display: axis.grid?.display,
      tickColor: baseColor.clone().setAlpha(0.5).toString(),
      color: baseColor.clone().setAlpha(0.1).toString()
    },
    ticks: {
      display: ticks.display !== false,
      color: fontColor || baseColor.clone().setAlpha(0.5).toString(),
      font: { size: fontSize, family: fontFamily },
      callback: function (value: any) {
        if (!Number.isNaN(Number(value))) {
          return Number(value).toLocaleString()
        }
        return value
      }
    }
  }

  // autoMin/autoMax handling
  if (ticks.autoMin !== true && ticks.min != null) {
    result.min = ticks.min
  }
  if (ticks.autoMax !== true && ticks.max != null) {
    result.max = ticks.max
  }
  if (ticks.beginAtZero) {
    result.beginAtZero = true
  }

  return result
}

function _buildLegend(
  legend: SceneChart.ChartLegendOptions,
  { fontSize, fontFamily, fontColor, theme }: { fontSize?: number; fontFamily?: string; fontColor?: string; theme?: string }
): SceneChart.ChartLegendOptions {
  const baseColor = _getBaseColorFromTheme(theme)
  const color = fontColor || baseColor.clone().setAlpha(0.5).toString()

  return {
    display: legend.display,
    position: legend.position,
    labels: {
      boxWidth: 15,
      color,
      font: { size: fontSize, family: fontFamily }
    }
  }
}

function _buildTooltip({ fontSize, fontFamily }: { fontSize?: number; fontFamily?: string }): SceneChart.ChartTooltipOptions {
  return {
    enabled: true,
    mode: 'index',
    intersect: false,
    titleFont: { size: fontSize, family: fontFamily },
    bodyFont: { size: fontSize, family: fontFamily },
    footerFont: { size: fontSize, family: fontFamily },
    callbacks: {
      label: function (context: any) {
        const dataset = context.dataset
        const value = context.parsed?.y ?? context.parsed ?? context.raw
        const label = dataset.label || context.label || ''
        const format = dataset.valueFormat || ''
        const prefix = dataset.valuePrefix || ''
        const suffix = dataset.valueSuffix || ''
        const stringValue = format ? formatText(format, Number(value)) : Number(value).toLocaleString()
        return `${label}: ${prefix + stringValue + suffix}`
      }
    }
  }
}

function _setupSeriesVisuals(series: SceneChart.ChartDataSets, chartType: string) {
  const type = series.type || chartType
  const color = (() => {
    switch (type) {
      case 'line':
      case 'radar':
        return series.color || series.borderColor
      default:
        return series.color || series.backgroundColor
    }
  })()

  switch (type) {
    case 'bar':
    case 'horizontalBar':
      series.borderColor = series.backgroundColor = color as string
      break
    case 'line':
    case 'radar':
      series.pointBackgroundColor = series.pointBorderColor = series.borderColor = series.backgroundColor = color as string
      series.pointBorderWidth = ((series.borderWidth as number) || 0) * 0.5
      series.pointHoverRadius = series.pointRadius
      if (series.fill == undefined) series.fill = false
      // v2 lineTension → v4 tension
      if ((series as any).lineTension != null && series.tension == null) {
        series.tension = (series as any).lineTension
      }
      break
    default:
      series.borderColor = series.backgroundColor = color as string
      break
  }

  series.dataLabelAnchor = series.dataLabelAnchor || 'center'
  series.dataLabelOffset = series.dataLabelOffset || 0
  series.dataLabelRotation = series.dataLabelRotation || 0
}

function _getBaseColorFromTheme(theme?: string) {
  return new TinyColor(theme === 'light' ? '#fff' : '#000')
}
